/**
 * Flux Image Generation Service
 * Connects to Django backend for Flux AI-powered image generation
 */

import axios from 'axios';

// Django backend API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';

/**
 * Get authentication token from localStorage
 */
const getAuthToken = () => {
  // Try both 'token' and 'access_token' for compatibility
  const token = localStorage.getItem('token') || localStorage.getItem('access_token');
  console.log('🔑 Auth token retrieved:', token ? 'Found' : 'Not found');
  return token;
};

/**
 * Generate carousel images using Flux API via Django backend
 * @param {Array} slides - Array of slides with header, description, visualPrompt
 * @param {string} platform - Platform preset (e.g., 'instagram-1-1', 'linkedin-4-5')
 * @param {Array} imageStyles - Array of selected image styles
 * @param {string} carouselTitle - Title/topic of the carousel
 * @returns {Promise<Object>} Generated images data
 */
export const generateCarouselImages = async (slides, platform, imageStyles = [], carouselTitle = '') => {
  try {
    const token = getAuthToken();
    
    if (!token) {
      throw new Error('Authentication required. Please log in.');
    }

    console.log('🎨 Generating images with Flux API...', { 
      slideCount: slides.length, 
      platform, 
      styles: imageStyles,
      title: carouselTitle
    });

    const response = await axios.post(
      `${API_BASE_URL}/ai/generate/images/`,
      {
        slides,
        platform,
        image_styles: imageStyles,
        carousel_title: carouselTitle
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        timeout: 300000 // 5 minutes timeout for image generation
      }
    );

    console.log('✅ Flux API Response:', response.data);
    console.log('   - Generated images:', response.data.generated_images);
    
    return response.data;
  } catch (error) {
    console.error('Flux image generation error:', error);
    
    // Handle different error types
    if (error.response) {
      // Server responded with error
      const status = error.response.status;
      const message = error.response.data?.message || error.response.data?.error || 'Image generation failed';
      
      if (status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (status === 402) {
        throw new Error('Quota exceeded. Please upgrade your plan.');
      } else if (status === 500) {
        throw new Error(`Image generation failed: ${message}`);
      } else {
        throw new Error(message);
      }
    } else if (error.request) {
      // Request made but no response
      throw new Error('Network error. Please check your connection.');
    } else {
      // Something else went wrong
      throw new Error(error.message || 'An unexpected error occurred');
    }
  }
};

/**
 * Generate a single test image (for testing purposes)
 * @param {string} prompt - Image generation prompt
 * @param {number} width - Image width
 * @param {number} height - Image height
 * @returns {Promise<Object>} Generated image data
 */
export const generateTestImage = async (prompt, width = 1080, height = 1080) => {
  try {
    const token = getAuthToken();
    
    if (!token) {
      throw new Error('Authentication required. Please log in.');
    }

    // For testing, we can create a simple endpoint or use the main one with a single slide
    const testSlide = [{
      slideNumber: 1,
      header: 'Test Image',
      description: 'Test description',
      visualPrompt: prompt
    }];

    const response = await generateCarouselImages(testSlide, 'instagram-1-1', []);
    return response.generated_images[0];
  } catch (error) {
    console.error('Test image generation error:', error);
    throw error;
  }
};

/**
 * Generate carousel images asynchronously (returns task ID immediately)
 * @param {Array} slides - Array of slides with header, description, visualPrompt
 * @param {string} platform - Platform preset (e.g., 'instagram-1-1', 'linkedin-4-5')
 * @param {Array} imageStyles - Array of selected image styles
 * @param {string} carouselTitle - Title/topic of the carousel
 * @param {string} backgroundType - 'colors' or 'styles'
 * @param {Object} colorSettings - Color/gradient/pattern settings (only for backgroundType='colors')
 * @returns {Promise<Object>} Task information with task_id
 */
export const generateCarouselImagesAsync = async (slides, platform, imageStyles = [], carouselTitle = '', carouselKeywords = '', backgroundType = 'styles', colorSettings = null, logoId = null, logoPosition = 'bottom-right') => {
  try {
    const token = getAuthToken();
    
    if (!token) {
      throw new Error('Authentication required. Please log in.');
    }

    console.log('🚀 Starting async image generation...', { 
      slideCount: slides.length, 
      platform, 
      backgroundType,
      styles: imageStyles,
      colorSettings,
      title: carouselTitle,
      keywords: carouselKeywords,
      logoId,
      logoPosition
    });

    const requestBody = {
      slides,
      platform,
      background_type: backgroundType,
      image_styles: imageStyles,
      color_settings: colorSettings,
      carousel_title: carouselTitle,
      carousel_keywords: carouselKeywords
    };

    // Add logo parameters if logo is selected
    if (logoId) {
      requestBody.logo_id = logoId;
      requestBody.logo_position = logoPosition;
    }

    const response = await axios.post(
      `${API_BASE_URL}/ai/generate/images/async/`,
      requestBody,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        timeout: 10000 // 10 seconds timeout for starting task
      }
    );

    console.log('✅ Task queued:', response.data);
    return response.data;
  } catch (error) {
    console.error('Async image generation error:', error);
    
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.message || error.response.data?.error || 'Failed to start generation';
      
      if (status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (status === 402) {
        const data = error.response.data;
        throw new Error(`Insufficient credits. You need ${data.required_credits} credits but only have ${data.available_credits}.`);
      } else {
        throw new Error(message);
      }
    } else if (error.request) {
      throw new Error('Network error. Please check your connection.');
    } else {
      throw new Error(error.message || 'An unexpected error occurred');
    }
  }
};

/**
 * Check the status of an async image generation task
 * @param {string} taskId - Task ID returned from generateCarouselImagesAsync
 * @returns {Promise<Object>} Task status information
 */
export const checkTaskStatus = async (taskId) => {
  try {
    const token = getAuthToken();
    
    if (!token) {
      throw new Error('Authentication required. Please log in.');
    }

    const response = await axios.get(
      `${API_BASE_URL}/ai/task-status/${taskId}/`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        timeout: 10000
      }
    );

    return response.data;
  } catch (error) {
    console.error('Task status check error:', error);
    
    if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    }
    
    throw new Error('Failed to check task status');
  }
};

/**
 * Poll task status until completion
 * @param {string} taskId - Task ID to poll
 * @param {Function} onProgress - Callback for progress updates
 * @param {number} maxAttempts - Maximum polling attempts (default: 300 = 10 minutes)
 * @returns {Promise<Object>} Final result when task completes
 */
export const pollTaskStatus = async (taskId, onProgress, maxAttempts = 300) => {
  let attempts = 0;
  
  return new Promise((resolve, reject) => {
    const pollInterval = setInterval(async () => {
      try {
        attempts++;
        
        if (attempts > maxAttempts) {
          clearInterval(pollInterval);
          reject(new Error('Task polling timeout. The generation is taking longer than expected.'));
          return;
        }
        
        const status = await checkTaskStatus(taskId);
        
        // Call progress callback if provided
        if (onProgress) {
          onProgress(status);
        }
        
        // Check if task is complete
        if (status.status === 'SUCCESS') {
          clearInterval(pollInterval);
          console.log('✅ Task completed successfully:', status.result);
          resolve(status.result);
        } else if (status.status === 'FAILURE') {
          clearInterval(pollInterval);
          console.error('❌ Task failed:', status.error);
          reject(new Error(status.error || 'Image generation failed'));
        }
        // Continue polling for PENDING, STARTED, PROGRESS states
        
      } catch (error) {
        clearInterval(pollInterval);
        reject(error);
      }
    }, 2000); // Poll every 2 seconds
  });
};

/**
 * Cancel a running task (if backend supports it)
 * @param {string} taskId - Task ID to cancel
 * @returns {Promise<Object>} Cancellation result
 */
export const cancelTask = async (taskId) => {
  try {
    const token = getAuthToken();
    
    if (!token) {
      throw new Error('Authentication required. Please log in.');
    }

    // Note: Backend endpoint for cancellation would need to be implemented
    const response = await axios.post(
      `${API_BASE_URL}/ai/task-cancel/${taskId}/`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Task cancellation error:', error);
    throw new Error('Failed to cancel task');
  }
};

const fluxService = {
  generateCarouselImages,
  generateTestImage,
  generateCarouselImagesAsync,
  checkTaskStatus,
  pollTaskStatus,
  cancelTask
};

export default fluxService;
